import { Request, Response } from "express";
import mongoose from "mongoose";

import {
  Alert,
  ALERT_PRIORITY,
  ALERT_STATUS,
  ALERT_TYPE,
  Class,
  type IAlert,
  type ITeacher,
  Notification,
  NOTIFICATION_PRIORITY,
  NOTIFICATION_TYPE,
  School,
  Student,
  Teacher,
} from "../models";
import { pushNotificationService } from "../services/push-notification-service";
import { logger } from "../utils/logger";
import { asObjectId } from "../utils/object-id";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
}

class AlertController {
  /**
   * Create an escort or emergency alert (Student)
   * @route POST /api/v1/student/alerts
   * @access Private (Student)
   */
  async createAlert(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { type, title, description, location, priority } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      if (!type || !title || !description) {
        res.status(400).json({
          success: false,
          message: "Missing required fields: type, title, description",
        } as APIResponse);
        return;
      }

      if (![ALERT_TYPE.ESCORT, ALERT_TYPE.EMERGENCY].includes(type)) {
        res.status(400).json({
          success: false,
          message: "Invalid alert type. Must be 'escort' or 'emergency'",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
        } as APIResponse);
        return;
      }

      // Emergency alerts are always urgent
      const alertPriority =
        type === ALERT_TYPE.EMERGENCY
          ? ALERT_PRIORITY.URGENT
          : priority || ALERT_PRIORITY.MEDIUM;

      // Normalize location (GeoJSON Point)
      let normalizedLocation: any = {};
      if (location) {
        const coords = location.coordinates;
        normalizedLocation =
          coords && Array.isArray(coords) && coords.length === 2
            ? {
                type: "Point",
                coordinates: [Number(coords[0]), Number(coords[1])],
                description: location.description || location.address,
                address: location.address,
                building: location.building,
                room: location.room,
              }
            : {
                type: "Point",
                description: location.description || location.address,
                address: location.address,
                building: location.building,
                room: location.room,
              };
      }

      const alert = new Alert({
        studentId: student._id,
        schoolId: student.schoolId,
        type,
        title,
        description,
        location: normalizedLocation,
        priority: alertPriority,
        status: ALERT_STATUS.PENDING,
        requestedAt: new Date(),
      });

      await alert.save();

      // Send push notification to school
      await pushNotificationService.sendEmergencyAlert(
        student.schoolId,
        student.schoolId,
        {
          alertId: alert.id,
          alertType: type,
          location:
            normalizedLocation?.address || normalizedLocation?.description,
          personName: student.fullName,
        },
      );

      // Create urgent notification for school (database record)
      await this.createNotification(
        student.schoolId.toString(),
        type === ALERT_TYPE.EMERGENCY
          ? NOTIFICATION_TYPE.EMERGENCY_ALERT
          : NOTIFICATION_TYPE.ESCORT_ALERT,
        type === ALERT_TYPE.EMERGENCY ? "Emergency Alert" : "Escort Alert",
        `${student.fullName} has requested ${type === ALERT_TYPE.EMERGENCY ? "emergency" : "escort"} assistance: ${title}`,
        type === ALERT_TYPE.EMERGENCY
          ? NOTIFICATION_PRIORITY.URGENT
          : NOTIFICATION_PRIORITY.HIGH,
        `/school/alerts/${alert.id}`,
      );

      logger.info(`Alert created: ${alert.id}`, {
        alertId: alert.id,
        type,
        studentId: student.id,
        schoolId: student.schoolId,
      });

      res.status(201).json({
        success: true,
        message: `${type === ALERT_TYPE.EMERGENCY ? "Emergency" : "Escort"} alert created successfully`,
        data: {
          alert: this.formatAlertResponse(alert),
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Create alert error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to create alert",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all alerts for authenticated student
   * @route GET /api/v1/student/alerts
   * @access Private (Student)
   */
  async getStudentAlerts(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { type, status, page = 1, limit = 20 } = req.query;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
        } as APIResponse);
        return;
      }

      const filterQuery: any = { studentId: student._id };

      if (type) filterQuery.type = type;
      if (status) filterQuery.status = status;

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const alerts = await Alert.find(filterQuery)
        .populate("acknowledgedBy", "email")
        .populate("assignedTo", "email fullName")
        .populate("resolvedBy", "email")
        .sort({ requestedAt: -1 })
        .skip(skip)
        .limit(limitNum);

      const total = await Alert.countDocuments(filterQuery);

      res.status(200).json({
        success: true,
        message: "Alerts retrieved successfully",
        data: {
          alerts: alerts.map(alert => this.formatAlertResponse(alert)),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get student alerts error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve alerts",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all alerts for school panel
   * @route GET /api/v1/school/alerts
   * @access Private (School Admin)
   */
  async getSchoolAlerts(req: AuthRequest, res: Response): Promise<void> {
    try {
      const schoolId = req.user?.schoolId;
      const {
        type,
        status,
        priority,
        studentId,
        page = 1,
        limit = 20,
        sortBy = "requestedAt",
        sortOrder = "desc",
      } = req.query;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        schoolId: new mongoose.Types.ObjectId(schoolId),
      };

      if (type) filterQuery.type = type;
      if (status) filterQuery.status = status;
      if (priority) filterQuery.priority = priority;
      if (studentId) {
        filterQuery.studentId = new mongoose.Types.ObjectId(
          studentId as string,
        );
      }
      // Note: teacherId filter can be added if needed for school panel

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      const sort: any = {};
      sort[sortBy as string] = sortOrder === "asc" ? 1 : -1;

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const alerts = await Alert.find(filterQuery)
        .populate("student", "fullName studentId profilePicture phone")
        .populate("teacher", "fullName employeeId profilePicture")
        .populate("acknowledgedBy", "email fullName")
        .populate("assignedTo", "email fullName")
        .populate("resolvedBy", "email fullName")
        .sort(sort)
        .skip(skip)
        .limit(limitNum);

      const total = await Alert.countDocuments(filterQuery);

      res.status(200).json({
        success: true,
        message: "Alerts retrieved successfully",
        data: {
          alerts: alerts.map(alert => this.formatAlertResponse(alert)),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get school alerts error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve alerts",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Acknowledge an alert
   * @route PUT /api/v1/school/alerts/:id/acknowledge
   * @access Private (School Admin)
   */
  async acknowledgeAlert(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid alert ID",
        } as APIResponse);
        return;
      }

      if (!userId || !schoolId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const alert = await Alert.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!alert) {
        res.status(404).json({
          success: false,
          message: "Alert not found",
        } as APIResponse);
        return;
      }

      if (alert.status !== ALERT_STATUS.PENDING) {
        res.status(400).json({
          success: false,
          message: `Alert is already ${alert.status}`,
        } as APIResponse);
        return;
      }

      await alert.acknowledge(new mongoose.Types.ObjectId(userId));

      logger.info(`Alert acknowledged: ${alert.id}`, {
        alertId: alert.id,
        acknowledgedBy: userId,
      });

      // Populate for formatted response
      await alert.populate(
        "student",
        "fullName studentId profilePicture phone",
      );
      await alert.populate("teacher", "fullName employeeId profilePicture");
      await alert.populate("acknowledgedBy", "email fullName");

      res.status(200).json({
        success: true,
        message: "Alert acknowledged successfully",
        data: {
          alert: this.formatAlertResponse(alert),
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Acknowledge alert error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to acknowledge alert",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Assign an alert
   * @route PUT /api/v1/school/alerts/:id/assign
   * @access Private (School Admin)
   */
  async assignAlert(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const { assignedTo, estimatedTime } = req.body;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid alert ID",
        } as APIResponse);
        return;
      }

      if (!assignedTo) {
        res.status(400).json({
          success: false,
          message: "assignedTo is required",
        } as APIResponse);
        return;
      }

      if (!schoolId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const alert = await Alert.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!alert) {
        res.status(404).json({
          success: false,
          message: "Alert not found",
        } as APIResponse);
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-deprecated
      await alert.assign(new mongoose.Types.ObjectId(assignedTo));
      if (estimatedTime) {
        alert.estimatedTime = estimatedTime;
        await alert.save();
      }

      logger.info(`Alert assigned: ${alert.id}`, {
        alertId: alert.id,
        assignedTo,
      });

      // Populate for formatted response
      await alert.populate(
        "student",
        "fullName studentId profilePicture phone",
      );
      await alert.populate("teacher", "fullName employeeId profilePicture");
      await alert.populate("assignedTo", "email fullName");

      res.status(200).json({
        success: true,
        message: "Alert assigned successfully",
        data: {
          alert: this.formatAlertResponse(alert),
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Assign alert error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to assign alert",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Resolve an alert
   * @route PUT /api/v1/school/alerts/:id/resolve
   * @access Private (School Admin)
   */
  async resolveAlert(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const { resolution, actualTime } = req.body;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid alert ID",
        } as APIResponse);
        return;
      }

      if (!resolution) {
        res.status(400).json({
          success: false,
          message: "Resolution is required",
        } as APIResponse);
        return;
      }

      if (!userId || !schoolId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const alert = await Alert.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!alert) {
        res.status(404).json({
          success: false,
          message: "Alert not found",
        } as APIResponse);
        return;
      }

      await alert.resolve(
        new mongoose.Types.ObjectId(userId),
        resolution,
        actualTime,
      );

      logger.info(`Alert resolved: ${alert.id}`, {
        alertId: alert.id,
        resolvedBy: userId,
      });

      // Populate for formatted response
      await alert.populate(
        "student",
        "fullName studentId profilePicture phone",
      );
      await alert.populate("teacher", "fullName employeeId profilePicture");
      await alert.populate("resolvedBy", "email fullName");

      res.status(200).json({
        success: true,
        message: "Alert resolved successfully",
        data: {
          alert: this.formatAlertResponse(alert),
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Resolve alert error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to resolve alert",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Cancel an alert (Student)
   * @route PUT /api/v1/student/alerts/:id/cancel
   * @access Private (Student)
   */
  async cancelAlert(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const userId = req.user?.userId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid alert ID",
        } as APIResponse);
        return;
      }

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
        } as APIResponse);
        return;
      }

      const alert = await Alert.findOne({
        _id: id,
        studentId: student._id,
      });

      if (!alert) {
        res.status(404).json({
          success: false,
          message: "Alert not found",
        } as APIResponse);
        return;
      }

      if (
        alert.status === ALERT_STATUS.RESOLVED ||
        alert.status === ALERT_STATUS.CANCELLED
      ) {
        res.status(400).json({
          success: false,
          message: `Cannot cancel alert with status: ${alert.status}`,
        } as APIResponse);
        return;
      }

      await alert.cancel();

      logger.info(`Alert cancelled: ${alert.id}`, {
        alertId: alert.id,
        studentId: student.id,
      });

      // Populate for formatted response
      await alert.populate(
        "student",
        "fullName studentId profilePicture phone",
      );
      await alert.populate("teacher", "fullName employeeId profilePicture");

      res.status(200).json({
        success: true,
        message: "Alert cancelled successfully",
        data: {
          alert: this.formatAlertResponse(alert),
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Cancel alert error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to cancel alert",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Create an escort or emergency alert (Teacher)
   * @route POST /api/v1/teacher/alerts
   * @access Private (Teacher)
   */
  async createTeacherAlert(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { type, title, description, location, priority } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      if (!type || !title || !description) {
        res.status(400).json({
          success: false,
          message: "Missing required fields: type, title, description",
        } as APIResponse);
        return;
      }

      if (![ALERT_TYPE.ESCORT, ALERT_TYPE.EMERGENCY].includes(type)) {
        res.status(400).json({
          success: false,
          message: "Invalid alert type. Must be 'escort' or 'emergency'",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
        } as APIResponse);
        return;
      }

      // Emergency alerts are always urgent
      const alertPriority =
        type === ALERT_TYPE.EMERGENCY
          ? ALERT_PRIORITY.URGENT
          : priority || ALERT_PRIORITY.MEDIUM;

      // Normalize location (GeoJSON Point)
      let normalizedLocation: any = {};
      if (location) {
        const coords = location.coordinates;
        normalizedLocation =
          coords && Array.isArray(coords) && coords.length === 2
            ? {
                type: "Point",
                coordinates: [Number(coords[0]), Number(coords[1])],
                description: location.description || location.address,
                address: location.address,
                building: location.building,
                room: location.room,
              }
            : {
                type: "Point",
                description: location.description || location.address,
                address: location.address,
                building: location.building,
                room: location.room,
              };
      }

      const alert = new Alert({
        teacherId: teacher._id,
        schoolId: teacher.schoolId,
        type,
        title,
        description,
        location: normalizedLocation,
        priority: alertPriority,
        status: ALERT_STATUS.PENDING,
        requestedAt: new Date(),
      });

      await alert.save();

      // Create urgent notification for school
      await this.createNotification(
        teacher.schoolId.toString(),
        type === ALERT_TYPE.EMERGENCY
          ? NOTIFICATION_TYPE.EMERGENCY_ALERT
          : NOTIFICATION_TYPE.ESCORT_ALERT,
        type === ALERT_TYPE.EMERGENCY ? "Emergency Alert" : "Escort Alert",
        `${teacher.fullName} has requested ${type === ALERT_TYPE.EMERGENCY ? "emergency" : "escort"} assistance: ${title}`,
        type === ALERT_TYPE.EMERGENCY
          ? NOTIFICATION_PRIORITY.URGENT
          : NOTIFICATION_PRIORITY.HIGH,
        `/school/alerts/${alert.id}`,
      );

      logger.info(`Teacher alert created: ${alert.id}`, {
        alertId: alert.id,
        type,
        teacherId: teacher.id,
        schoolId: teacher.schoolId,
      });

      res.status(201).json({
        success: true,
        message: `${type === ALERT_TYPE.EMERGENCY ? "Emergency" : "Escort"} alert created successfully`,
        data: {
          alert: this.formatAlertResponse(alert),
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Create teacher alert error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to create alert",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all alerts for authenticated teacher
   * @route GET /api/v1/teacher/alerts
   * @access Private (Teacher)
   */
  async getTeacherAlerts(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { type, status, page = 1, limit = 20 } = req.query;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
        } as APIResponse);
        return;
      }

      const teacherClasses = await Class.find({
        schoolId: teacher.schoolId,
        teacherIds: teacher._id,
      }).select("studentIds");

      const studentIds = new Set<string>();
      for (const classItem of teacherClasses) {
        classItem.studentIds?.forEach((id: mongoose.Types.ObjectId) => {
          studentIds.add(id.toString());
        });
      }

      const filterQuery: Record<string, unknown> = {
        schoolId: teacher.schoolId,
        $or: [
          {
            studentId: {
              $in: [...studentIds].map(id => new mongoose.Types.ObjectId(id)),
            },
          },
          { teacherId: teacher._id },
        ],
      };

      if (type) filterQuery.type = type;
      if (status) {
        filterQuery.status =
          status === "active" ? ALERT_STATUS.PENDING : status;
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      const alerts = await Alert.find({ ...filterQuery })
        .populate("student", "fullName studentId profilePicture phone")
        .populate("teacher", "fullName employeeId profilePicture")
        .populate("acknowledgedBy", "email fullName")
        .populate("assignedTo", "email fullName")
        .populate("resolvedBy", "email fullName")
        .sort({ requestedAt: -1 })
        .skip(skip)
        .limit(limitNum);

      const total = await Alert.countDocuments(filterQuery);

      res.status(200).json({
        success: true,
        message: "Alerts retrieved successfully",
        data: {
          alerts: alerts.map(alert => this.formatAlertResponse(alert)),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get teacher alerts error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve alerts",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  private async teacherCanAccessAlert(
    teacher: ITeacher,
    alert: IAlert,
  ): Promise<boolean> {
    if (alert.teacherId?.toString() === asObjectId(teacher._id).toString()) {
      return true;
    }

    if (!alert.studentId) {
      return false;
    }

    const teacherClasses = await Class.find({
      schoolId: teacher.schoolId,
      teacherIds: teacher._id,
      studentIds: alert.studentId,
    }).select("_id");

    return teacherClasses.length > 0;
  }

  async getTeacherAlertById(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { id } = req.params;

      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!teacher) {
        res.status(404).json({ success: false, message: "Teacher not found" });
        return;
      }

      const alert = await Alert.findOne({
        _id: id,
        schoolId: teacher.schoolId,
      })
        .populate("student", "fullName studentId profilePicture phone")
        .populate("teacher", "fullName employeeId profilePicture")
        .populate("acknowledgedBy", "email fullName")
        .populate("assignedTo", "email fullName")
        .populate("resolvedBy", "email fullName");

      if (!alert || !(await this.teacherCanAccessAlert(teacher, alert))) {
        res.status(404).json({ success: false, message: "Alert not found" });
        return;
      }

      res.status(200).json({
        success: true,
        message: "Alert retrieved successfully",
        data: { alert: this.formatAlertResponse(alert) },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get teacher alert error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve alert",
      } as APIResponse);
    }
  }

  async acknowledgeTeacherAlert(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const { id } = req.params;
      const userId = req.user?.userId;

      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!teacher) {
        res.status(404).json({ success: false, message: "Teacher not found" });
        return;
      }

      const alert = await Alert.findOne({
        _id: id,
        schoolId: teacher.schoolId,
      });

      if (!alert || !(await this.teacherCanAccessAlert(teacher, alert))) {
        res.status(404).json({ success: false, message: "Alert not found" });
        return;
      }

      if (alert.status !== ALERT_STATUS.PENDING) {
        res.status(400).json({
          success: false,
          message: `Alert is already ${alert.status}`,
        });
        return;
      }

      await alert.acknowledge(new mongoose.Types.ObjectId(userId));
      await alert.populate(
        "student",
        "fullName studentId profilePicture phone",
      );
      await alert.populate("teacher", "fullName employeeId profilePicture");
      await alert.populate("acknowledgedBy", "email fullName");

      res.status(200).json({
        success: true,
        message: "Alert acknowledged successfully",
        data: { alert: this.formatAlertResponse(alert) },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Acknowledge teacher alert error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to acknowledge alert",
      } as APIResponse);
    }
  }

  async resolveTeacherAlert(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const { resolution, actualTime } = req.body;
      const userId = req.user?.userId;

      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!teacher) {
        res.status(404).json({ success: false, message: "Teacher not found" });
        return;
      }

      const alert = await Alert.findOne({
        _id: id,
        schoolId: teacher.schoolId,
      });

      if (!alert || !(await this.teacherCanAccessAlert(teacher, alert))) {
        res.status(404).json({ success: false, message: "Alert not found" });
        return;
      }

      if (
        alert.status === ALERT_STATUS.RESOLVED ||
        alert.status === ALERT_STATUS.CANCELLED
      ) {
        res.status(400).json({
          success: false,
          message: `Alert is already ${alert.status}`,
        });
        return;
      }

      await alert.resolve(
        new mongoose.Types.ObjectId(userId),
        resolution || "Resolved by teacher",
        actualTime,
      );
      await alert.populate(
        "student",
        "fullName studentId profilePicture phone",
      );
      await alert.populate("teacher", "fullName employeeId profilePicture");
      await alert.populate("resolvedBy", "email fullName");

      res.status(200).json({
        success: true,
        message: "Alert resolved successfully",
        data: { alert: this.formatAlertResponse(alert) },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Resolve teacher alert error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to resolve alert",
      } as APIResponse);
    }
  }

  async updateAlertLocation(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const { location } = req.body;
      const userId = req.user?.userId;

      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!student) {
        res.status(404).json({ success: false, message: "Student not found" });
        return;
      }

      const alert = await Alert.findOne({
        _id: id,
        studentId: student._id,
      });

      if (!alert) {
        res.status(404).json({ success: false, message: "Alert not found" });
        return;
      }

      if (
        alert.status === ALERT_STATUS.RESOLVED ||
        alert.status === ALERT_STATUS.CANCELLED
      ) {
        res.status(400).json({
          success: false,
          message: `Cannot update location for alert with status: ${alert.status}`,
        });
        return;
      }

      const coords = location?.coordinates;
      if (coords && Array.isArray(coords) && coords.length === 2) {
        alert.location = {
          type: "Point",
          coordinates: [Number(coords[0]), Number(coords[1])],
          description: location.description || location.address,
          address: location.address,
          building: location.building,
          room: location.room,
        };
        await alert.save();
      } else {
        res.status(400).json({
          success: false,
          message: "location.coordinates [longitude, latitude] is required",
        });
        return;
      }

      res.status(200).json({
        success: true,
        message: "Alert location updated successfully",
        data: { alert: this.formatAlertResponse(alert) },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Update alert location error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to update alert location",
      } as APIResponse);
    }
  }

  /**
   * Cancel an alert (Teacher)
   * @route PUT /api/v1/teacher/alerts/:id/cancel
   * @access Private (Teacher)
   */
  async cancelTeacherAlert(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const userId = req.user?.userId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid alert ID",
        } as APIResponse);
        return;
      }

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
        } as APIResponse);
        return;
      }

      const alert = await Alert.findOne({
        _id: id,
        teacherId: teacher._id,
      });

      if (!alert) {
        res.status(404).json({
          success: false,
          message: "Alert not found",
        } as APIResponse);
        return;
      }

      if (
        alert.status === ALERT_STATUS.RESOLVED ||
        alert.status === ALERT_STATUS.CANCELLED
      ) {
        res.status(400).json({
          success: false,
          message: `Cannot cancel alert with status: ${alert.status}`,
        } as APIResponse);
        return;
      }

      await alert.cancel();

      logger.info(`Teacher alert cancelled: ${alert.id}`, {
        alertId: alert.id,
        teacherId: teacher.id,
      });

      res.status(200).json({
        success: true,
        message: "Alert cancelled successfully",
        data: {
          alert: this.formatAlertResponse(alert),
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Cancel teacher alert error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to cancel alert",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Create emergency alert (Admin)
   * @route POST /api/v1/admin/alerts
   * @access Private (Admin)
   */
  async createAdminAlert(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const {
        studentId,
        teacherId,
        schoolId,
        type,
        title,
        description,
        location,
        priority,
      } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      if (!type || !title || !description) {
        res.status(400).json({
          success: false,
          message: "Missing required fields: type, title, description",
        } as APIResponse);
        return;
      }

      if (![ALERT_TYPE.ESCORT, ALERT_TYPE.EMERGENCY].includes(type)) {
        res.status(400).json({
          success: false,
          message: "Invalid alert type. Must be 'escort' or 'emergency'",
        } as APIResponse);
        return;
      }

      // Must provide exactly one: studentId, teacherId, or schoolId
      const providedIds = [studentId, teacherId, schoolId].filter(Boolean);
      if (providedIds.length !== 1) {
        res.status(400).json({
          success: false,
          message:
            "Exactly one of studentId, teacherId, or schoolId is required",
        } as APIResponse);
        return;
      }

      let alertSchoolId: mongoose.Types.ObjectId;
      let alertStudentId: mongoose.Types.ObjectId | undefined;
      let alertTeacherId: mongoose.Types.ObjectId | undefined;
      let alertTitle = title;
      const alertDescription = description;

      if (studentId) {
        const student = await Student.findById(studentId);
        if (!student) {
          res.status(404).json({
            success: false,
            message: "Student not found",
          } as APIResponse);
          return;
        }
        alertSchoolId = student.schoolId;
        alertStudentId = asObjectId(student._id);
        if (!title.includes(student.fullName)) {
          alertTitle = `${student.fullName} - ${title}`;
        }
      } else if (teacherId) {
        const teacher = await Teacher.findById(teacherId);
        if (!teacher) {
          res.status(404).json({
            success: false,
            message: "Teacher not found",
          } as APIResponse);
          return;
        }
        alertSchoolId = teacher.schoolId;
        alertTeacherId = asObjectId(teacher._id);
        if (!title.includes(teacher.fullName)) {
          alertTitle = `${teacher.fullName} - ${title}`;
        }
      } else if (schoolId) {
        const school = await School.findById(schoolId);
        if (!school) {
          res.status(404).json({
            success: false,
            message: "School not found",
          } as APIResponse);
          return;
        }
        alertSchoolId = asObjectId(school._id);
      } else {
        res.status(400).json({
          success: false,
          message: "Must provide studentId, teacherId, or schoolId",
        } as APIResponse);
        return;
      }

      // Emergency alerts are always urgent
      const alertPriority =
        type === ALERT_TYPE.EMERGENCY
          ? ALERT_PRIORITY.URGENT
          : priority || ALERT_PRIORITY.MEDIUM;

      // Normalize location
      let normalizedLocation: any = {};
      if (location) {
        const coords = location.coordinates;
        normalizedLocation =
          coords && Array.isArray(coords) && coords.length === 2
            ? {
                type: "Point",
                coordinates: [Number(coords[0]), Number(coords[1])],
                description: location.description || location.address,
                address: location.address,
                building: location.building,
                room: location.room,
              }
            : {
                type: "Point",
                description: location.description || location.address,
                address: location.address,
                building: location.building,
                room: location.room,
              };
      }

      const alert = new Alert({
        studentId: alertStudentId,
        teacherId: alertTeacherId,
        schoolId: alertSchoolId,
        createdBy: new mongoose.Types.ObjectId(userId),
        type,
        title: alertTitle,
        description: alertDescription,
        location: normalizedLocation,
        priority: alertPriority,
        status: ALERT_STATUS.PENDING,
        requestedAt: new Date(),
      });

      await alert.save();

      // Create notification for school
      await this.createNotification(
        alertSchoolId.toString(),
        type === ALERT_TYPE.EMERGENCY
          ? NOTIFICATION_TYPE.EMERGENCY_ALERT
          : NOTIFICATION_TYPE.ESCORT_ALERT,
        type === ALERT_TYPE.EMERGENCY ? "Emergency Alert" : "Escort Alert",
        `Admin created ${type === ALERT_TYPE.EMERGENCY ? "emergency" : "escort"} alert: ${alertTitle}`,
        type === ALERT_TYPE.EMERGENCY
          ? NOTIFICATION_PRIORITY.URGENT
          : NOTIFICATION_PRIORITY.HIGH,
        `/school/alerts/${alert.id}`,
      );

      logger.info(`Admin alert created: ${alert.id}`, {
        alertId: alert.id,
        type,
        createdBy: userId,
        schoolId: alertSchoolId,
      });

      res.status(201).json({
        success: true,
        message: `${type === ALERT_TYPE.EMERGENCY ? "Emergency" : "Escort"} alert created successfully`,
        data: {
          alert: this.formatAlertResponse(alert),
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Create admin alert error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to create alert",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Create emergency alert (School)
   * @route POST /api/v1/school/alerts
   * @access Private (School Admin)
   */
  async createSchoolAlert(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const {
        studentId,
        teacherId,
        type,
        title,
        description,
        location,
        priority,
      } = req.body;

      if (!userId || !schoolId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated or school context missing",
        } as APIResponse);
        return;
      }

      if (!type || !title || !description) {
        res.status(400).json({
          success: false,
          message: "Missing required fields: type, title, description",
        } as APIResponse);
        return;
      }

      if (![ALERT_TYPE.ESCORT, ALERT_TYPE.EMERGENCY].includes(type)) {
        res.status(400).json({
          success: false,
          message: "Invalid alert type. Must be 'escort' or 'emergency'",
        } as APIResponse);
        return;
      }

      // Must provide exactly one: studentId or teacherId
      if (!studentId && !teacherId) {
        res.status(400).json({
          success: false,
          message: "Either studentId or teacherId is required",
        } as APIResponse);
        return;
      }

      if (studentId && teacherId) {
        res.status(400).json({
          success: false,
          message: "Provide either studentId or teacherId, not both",
        } as APIResponse);
        return;
      }

      let alertStudentId: mongoose.Types.ObjectId | undefined;
      let alertTeacherId: mongoose.Types.ObjectId | undefined;
      let alertTitle = title;
      const alertDescription = description;

      if (studentId) {
        const student = await Student.findOne({
          _id: mongoose.Types.ObjectId.createFromHexString(studentId),
          schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
        });
        if (!student) {
          res.status(404).json({
            success: false,
            message: "Student not found or does not belong to your school",
          } as APIResponse);
          return;
        }
        alertStudentId = asObjectId(student._id);
        if (!title.includes(student.fullName)) {
          alertTitle = `${student.fullName} - ${title}`;
        }
      } else if (teacherId) {
        const teacher = await Teacher.findOne({
          _id: mongoose.Types.ObjectId.createFromHexString(teacherId),
          schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
        });
        if (!teacher) {
          res.status(404).json({
            success: false,
            message: "Teacher not found or does not belong to your school",
          } as APIResponse);
          return;
        }
        alertTeacherId = asObjectId(teacher._id);
        if (!title.includes(teacher.fullName)) {
          alertTitle = `${teacher.fullName} - ${title}`;
        }
      }

      // Emergency alerts are always urgent
      const alertPriority =
        type === ALERT_TYPE.EMERGENCY
          ? ALERT_PRIORITY.URGENT
          : priority || ALERT_PRIORITY.MEDIUM;

      // Normalize location
      let normalizedLocation: any = {};
      if (location) {
        const coords = location.coordinates;
        normalizedLocation =
          coords && Array.isArray(coords) && coords.length === 2
            ? {
                type: "Point",
                coordinates: [Number(coords[0]), Number(coords[1])],
                description: location.description || location.address,
                address: location.address,
                building: location.building,
                room: location.room,
              }
            : {
                type: "Point",
                description: location.description || location.address,
                address: location.address,
                building: location.building,
                room: location.room,
              };
      }

      const alert = new Alert({
        studentId: alertStudentId,
        teacherId: alertTeacherId,
        schoolId: new mongoose.Types.ObjectId(schoolId),
        createdBy: new mongoose.Types.ObjectId(userId),
        type,
        title: alertTitle,
        description: alertDescription,
        location: normalizedLocation,
        priority: alertPriority,
        status: ALERT_STATUS.PENDING,
        requestedAt: new Date(),
      });

      await alert.save();

      logger.info(`School alert created: ${alert.id}`, {
        alertId: alert.id,
        type,
        createdBy: userId,
        schoolId,
      });

      res.status(201).json({
        success: true,
        message: `${type === ALERT_TYPE.EMERGENCY ? "Emergency" : "Escort"} alert created successfully`,
        data: {
          alert: this.formatAlertResponse(alert),
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Create school alert error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to create alert",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Format alert response with relative timestamps and UI-friendly format
   * Matches the mobile UI format shown in the image
   */
  private formatAlertResponse(alert: any): any {
    const alertDoc = alert.toObject ? alert.toObject() : alert;
    const student = alertDoc.student;
    const teacher = alertDoc.teacher;

    // Generate formatted message like "Nick Harrison Wants a assistance urgent Location Provided"
    let formattedMessage = "";
    const personName = student?.fullName || teacher?.fullName || "Unknown";
    const hasLocation =
      alertDoc.location &&
      (alertDoc.location.address ||
        alertDoc.location.description ||
        alertDoc.location.building ||
        alertDoc.location.coordinates);

    formattedMessage = `${personName} Wants a assistance ${alertDoc.priority}${hasLocation ? " Location Provided" : ""}`;

    // Calculate relative timestamp
    const relativeTime = this.getRelativeTime(
      alertDoc.requestedAt || alertDoc.createdAt,
    );

    // Determine color bar (alternating purple/green based on type)
    // Emergency = purple, Escort = green (can be customized)
    const colorBar =
      alertDoc.type === ALERT_TYPE.EMERGENCY ? "purple" : "green";

    return {
      id: alertDoc.id || alertDoc._id?.toString(),
      type: alertDoc.type,
      title: alertDoc.title,
      description: alertDoc.description,
      formattedMessage, // UI-friendly message format matching mobile UI
      location: alertDoc.location,
      priority: alertDoc.priority,
      status: alertDoc.status,
      colorBar, // For UI display (purple/green)
      requestedAt: alertDoc.requestedAt,
      relativeTime, // "an hour ago", "20 mins ago", etc.
      timestamp: alertDoc.requestedAt || alertDoc.createdAt, // ISO timestamp for reference
      student: student
        ? {
            id: student.id || student._id?.toString(),
            fullName: student.fullName,
            studentId: student.studentId,
            profilePicture: student.profilePicture,
            phone: student.phone,
          }
        : null,
      teacher: teacher
        ? {
            id: teacher.id || teacher._id?.toString(),
            fullName: teacher.fullName,
            employeeId: teacher.employeeId,
            profilePicture: teacher.profilePicture,
          }
        : null,
      acknowledgedBy: alertDoc.acknowledgedBy
        ? {
            id:
              alertDoc.acknowledgedBy.id ||
              alertDoc.acknowledgedBy._id?.toString(),
            email: alertDoc.acknowledgedBy.email,
            fullName: alertDoc.acknowledgedBy.fullName,
          }
        : null,
      acknowledgedAt: alertDoc.acknowledgedAt,
      assignedTo: alertDoc.assignedTo
        ? {
            id: alertDoc.assignedTo.id || alertDoc.assignedTo._id?.toString(),
            email: alertDoc.assignedTo.email,
            fullName: alertDoc.assignedTo.fullName,
          }
        : null,
      resolvedBy: alertDoc.resolvedBy
        ? {
            id: alertDoc.resolvedBy.id || alertDoc.resolvedBy._id?.toString(),
            email: alertDoc.resolvedBy.email,
            fullName: alertDoc.resolvedBy.fullName,
          }
        : null,
      resolvedAt: alertDoc.resolvedAt,
      resolution: alertDoc.resolution,
      estimatedTime: alertDoc.estimatedTime,
      actualTime: alertDoc.actualTime,
      createdAt: alertDoc.createdAt,
      updatedAt: alertDoc.updatedAt,
    };
  }

  /**
   * Get relative time string (e.g., "an hour ago", "20 mins ago")
   */
  private getRelativeTime(date: Date | string): string {
    const now = new Date();
    const past = typeof date === "string" ? new Date(date) : date;
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) {
      return "just now";
    } else if (diffMins < 60) {
      return `${diffMins} ${diffMins === 1 ? "min" : "mins"} ago`;
    } else if (diffHours < 24) {
      return diffHours === 1 ? "an hour ago" : `${diffHours} hours ago`;
    } else if (diffDays === 1) {
      return "yesterday";
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return past.toLocaleDateString();
    }
  }

  // Helper method
  private async createNotification(
    userId: string,
    type: string,
    title: string,
    message: string,
    priority: string,
    actionUrl?: string,
  ): Promise<void> {
    try {
      const notification = new Notification({
        userId: new mongoose.Types.ObjectId(userId),
        type: type as any,
        title,
        message,
        priority: priority as any,
        actionUrl,
        isRead: false,
      });
      await notification.save();
    } catch (error) {
      logger.error("Failed to create notification:", error);
    }
  }
}

export const alertController = new AlertController();
